import { Fragment, useState, useEffect } from 'react'
import { Heading } from '@leroy-merlin-br/backyard-react'

import emitter from 'scripts/utils/emitter'

import Loader from 'site/Loader'
import { Collapse } from 'site/Collapse'

import * as orderServices from 'checkout/services/order'

import OrderTitle from './components/Order/Title'
import OrderContent from './components/Order/Content'

const DEFAULTS = {
  messages: {
    error: {
      title: 'Ops! Ocorreu o seguinte erro:'
    }
  }
}

const AVAILABLE_PICKUP_STATUS = 'DISPONIVEL PARA RETIRADA'
const DELIVERY_ATTEMPT_STATUS = [
  'RESPONSÁVEL PELO RECEBIMENTO AUSENTE',
  'DIFICULDADE DE ACESSAR O LOCAL',
  'ENDEREÇO DE ENTREGA NÃO ENCONTRADO'
]
const CANCELLED_STATUS = 'CANCELADO'

const MyOrders = ({ fiscalId, saleOrderId, errors }) => {
  const [orders, setOrders] = useState([])
  const [ordersToPay, setOrdersToPay] = useState([])
  const [loading, setLoading] = useState(true)
  const [showErrorMsg, setShowErrorMsg] = useState(false)

  const onGetSuccess = ([dataOrders, dataOrdersToPay]) => {
    setOrders(dataOrders)
    setOrdersToPay(dataOrdersToPay)
    setLoading(false)
    setShowErrorMsg(false)
  }

  useEffect(() => {
    if (saleOrderId && orders.length) {
      const element = document.querySelector(
        '[data-section="' + saleOrderId + '"]'
      )

      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }, [orders, saleOrderId])

  useEffect(() => {
    Promise.all([
      orderServices.getOrders(fiscalId),
      orderServices.getOrdersToPay(fiscalId)
    ])
      .then(onGetSuccess)
      .catch(() => {
        setLoading(false)
        setShowErrorMsg(true)
      })
  }, [fiscalId])

  const shouldBeOpen = order => saleOrderId === order.id

  if (errors) {
    emitter.emit('stickyFeedback:error', {
      title: DEFAULTS.messages.error.title,
      content: errors
    })
  }

  const findSeparationProblem = ({ shipments }) => {
    const itemSeparationProblem = shipments.find(shipment =>
      shipment.items.find(item => item.status === 'Problema na separação')
    )
    return !!itemSeparationProblem
  }

  const allOrders = [...orders, ...ordersToPay]
  const isOrderExists = allOrders.some(order => order.id === saleOrderId)

  if (saleOrderId && allOrders.length > 0 && !isOrderExists) {
    emitter.emit('stickyFeedback:error', {
      title: 'Ops! ocorreu o seguinte erro:',
      content:
        'Pedido não encontrado. Verifique se o login realizado no aplicativo corresponte ao CPF informado ao assessor.'
    })
  }

  if (loading) {
    return <Loader />
  }

  if (showErrorMsg) {
    return (
      <div className="error-wrapper color-default">
        <span>
          No momento, não foi possível consultar os seus pedidos. Tente
          novamente mais tarde.
        </span>
      </div>
    )
  }

  const shouldNotShowOrders = !orders.length && !ordersToPay.length
  const shouldRenderOrders = orders.length > 0
  const shouldRenderOrdersToPay = ordersToPay.length > 0

  return (
    <Fragment>
      {shouldNotShowOrders && (
        <div className="error-wrapper color-default">
          <span>Você ainda não fez nenhum pedido, que tal aproveitar?</span>
        </div>
      )}

      {shouldRenderOrdersToPay && (
        <div className="orders-to-pay">
          <Heading as="h2" size="giga">
            Finalizar pedidos de Loja
          </Heading>

          {ordersToPay.map((order, index) => (
            <Collapse
              className="order-react-collapse"
              isOpen={shouldBeOpen(order, index)}
              key={index}
              title={
                <OrderTitle {...order} fiscalId={fiscalId} hasOrderToPay={1} />
              }
              content={
                <OrderContent {...order} isOpen={shouldBeOpen(order, index)} />
              }
            />
          ))}
        </div>
      )}

      {shouldRenderOrders && (
        <Fragment>
          <Heading as="h2" size="giga">
            Pedidos
          </Heading>

          <div className="row">
            <div className="col-xs-12">
              {orders.map((order, index) => {
                const hasOrderToPickUp =
                  order.status === AVAILABLE_PICKUP_STATUS
                const hasDeliveryAttempt = DELIVERY_ATTEMPT_STATUS.includes(
                  order.status
                )
                const hasCancelled = order.status === CANCELLED_STATUS
                const hasSeparationProblem = findSeparationProblem(order)

                return (
                  <Collapse
                    isOpen={shouldBeOpen(order, index)}
                    className="order-react-collapse"
                    key={index}
                    title={
                      <OrderTitle
                        {...order}
                        hasOrderToPickUp={hasOrderToPickUp}
                        hasSeparationProblem={hasSeparationProblem}
                        hasDeliveryAttempt={hasDeliveryAttempt}
                        hasCancelled={hasCancelled}
                        serviceCount={order.services?.items.length ?? 0}
                      />
                    }
                    content={
                      <OrderContent
                        {...order}
                        hasOrderToPickUp={hasOrderToPickUp}
                        hasSeparationProblem={hasSeparationProblem}
                        isOpen={shouldBeOpen(order, index)}
                      />
                    }
                  />
                )
              })}
            </div>
          </div>
        </Fragment>
      )}
    </Fragment>
  )
}

export default MyOrders
